import DOMPurify from 'dompurify'
/**
 * Record UTM and Matomo Martketing codes into sessionStorage
 */
export const setStoredUTMParams = (location: Location): void => {
  const searchParams = new URLSearchParams(location?.search)
  const parametersToStore = [
    'campaign',
    'source',
    'medium',
    'content',
    'placement'
  ]
  parametersToStore.forEach((param) => {
    const utmValue = searchParams.get(`utm_${param}`)
    const mtmValue = searchParams.get(`mtm_${param}`)
    const value = utmValue ?? mtmValue

    // Solo guardar si hay un valor real (no null ni cadena vacía)
    if (value && value.trim() !== '') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const sanitizedValue: string = DOMPurify.sanitize(value)
      const encodedValue = encodeURIComponent(sanitizedValue)
      sessionStorage.setItem(param, encodedValue)
    }
  })
}

/**
 * Get UTM and Matomo Martketing codes from sessionStorage
 */

type UTMParams = {
  campaign?: string | null
  source?: string | null
  medium?: string | null
  content?: string | null
  placement?: string | null
}

export const buildUTMTrackingUrl = (
  baseUrl: string,
  additionalParams: Record<string, string> = {}
): string => {
  try {
    // Usar dominio dummy para URLs relativas
    const url = new URL(baseUrl, 'https://dummy.com')
    const utmParams: UTMParams = getStoredUTMParams()

    // 🔥 UTM Parameters - SOLO añadir si NO existen ya
    if (!url.searchParams.has('utm_source') && utmParams.source) {
      url.searchParams.set('utm_source', utmParams.source)
    }
    if (!url.searchParams.has('utm_medium') && utmParams.medium) {
      url.searchParams.set('utm_medium', utmParams.medium)
    }
    if (!url.searchParams.has('utm_campaign') && utmParams.campaign) {
      url.searchParams.set('utm_campaign', utmParams.campaign)
    }
    if (!url.searchParams.has('utm_content') && utmParams.content) {
      url.searchParams.set('utm_content', utmParams.content)
    }
    if (!url.searchParams.has('mtm_placement') && utmParams.placement) {
      url.searchParams.set('mtm_placement', utmParams.placement)
    }

    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value)
    })

    // Retornar solo la parte relativa (pathname + search + hash)
    return url.pathname + url.search + url.hash
  } catch (error) {
    console.warn('Error building UTM URL:', error)
    return baseUrl
  }
}

export const getStoredUTMParams = (): UTMParams => {
  const getDecodedValue = (key: string): string | null => {
    const encodedValue = sessionStorage.getItem(key)
    if (!encodedValue) return null
    try {
      return decodeURIComponent(encodedValue)
    } catch (error) {
      console.warn(`Error decoding UTM parameter ${key}:`, error)
      return encodedValue // Fallback al valor original si no se puede decodificar
    }
  }

  return {
    campaign: getDecodedValue('campaign'),
    source: getDecodedValue('source'),
    medium: getDecodedValue('medium'),
    content: getDecodedValue('content'),
    placement: getDecodedValue('placement') || null
  }
}
